import type { UIMessage } from "../../types/chat";
import { AIAvatarRow } from "./AIAvatarRow";
import { MarkdownContent } from "./MarkdownContent";
import { CandidateMessageWrapper } from "./CandidateMessageWrapper";
import { GradientSpinner } from "../common/GradientSpinner";
import RestartLinear from "~icons/solar/restart-linear";

interface AIMessageBubbleProps {
  message: UIMessage;
  onRetry?: () => void;
}

export function AIMessageBubble({ message, onRetry }: AIMessageBubbleProps) {
  const isThinking = message.status === "streaming" && message.content === "";
  const isStreaming = message.status === "streaming" && message.content !== "";
  const isError = message.status === "error";
  const gradientId = `sparkle-gradient-${message.id}`;

  return (
    <AIAvatarRow>
      {/* Message content */}
      {isThinking ? (
        /* Thinking state: gradient-bordered pill with sparkle icon */
        <div
          role="status"
          aria-live="polite"
          className="mt-1 rounded-full bg-gradient-to-r from-gradient-start to-gradient-end p-px"
        >
          <div className="flex items-center gap-2 rounded-full bg-white px-3 py-1.5 lg:px-4 lg:py-2">
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              className="motion-safe:animate-spin"
            >
              <path
                d="M8 0L9.5 6.5L16 8L9.5 9.5L8 16L6.5 9.5L0 8L6.5 6.5L8 0Z"
                fill={`url(#${gradientId})`}
              />
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="16" y2="16">
                  <stop stopColor="#FF3072" />
                  <stop offset="1" stopColor="#750087" />
                </linearGradient>
              </defs>
            </svg>
            <span className="bg-gradient-to-r from-gradient-start to-gradient-end bg-clip-text text-body-sm lg:text-body-md font-normal text-transparent">
              Thinking...
            </span>
          </div>
        </div>
      ) : isError ? (
        /* Error state: light red container with retry button */
        <div
          role="alert"
          className="mt-1 flex items-start gap-2 rounded-lg bg-error-light px-3 py-2 lg:px-4 lg:py-3"
        >
          <p className="min-w-0 flex-1 text-body-sm lg:text-body-md text-error">
            {message.content}
          </p>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="inline-flex shrink-0 items-center gap-1 rounded-md bg-white px-2 py-1 lg:px-3 text-body-xs lg:text-body-sm font-medium text-error shadow-sm transition-colors hover:bg-red-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-error"
            >
              <RestartLinear className="size-4" />
              Retry
            </button>
          )}
        </div>
      ) : message.jdPath && message.status === "complete" ? (
        /* Candidate results message */
        <CandidateMessageWrapper jdPath={message.jdPath}>
          <MarkdownContent content={message.content} />
        </CandidateMessageWrapper>
      ) : (
        /* Streaming or complete text */
        <div className="mt-1 min-w-0">
          <MarkdownContent content={message.content} />
          {isStreaming && (
            <span className="ml-0.5 inline-block translate-y-[2px]">
              <GradientSpinner size={20} />
            </span>
          )}
        </div>
      )}
    </AIAvatarRow>
  );
}
